'use client';

import { Component, ErrorInfo, ReactNode } from 'react';
import { isDevelopment } from '../utils/env';
import { createClientLogger } from '../utils/logger';

const logger = createClientLogger('ErrorBoundary');

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Enterprise-grade error boundary with recovery mechanisms.
 *
 * Features:
 * - Custom fallback UI
 * - Error reporting/telemetry
 * - Automatic reset on prop changes
 * - Development vs production error details
 *
 * @example
 * ```tsx
 * <ErrorBoundary
 *   fallback={<ErrorFallback />}
 *   onError={(error) => analytics.track('error', error)}
 *   resetKeys={[user?.id]}
 * >
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Caught an error', error, { componentStack: errorInfo.componentStack });

    this.setState({
      error,
      errorInfo,
    });

    // Report error to monitoring service
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    if (hasError && prevProps.resetKeys !== resetKeys) {
      if (resetKeys?.some((resetKey, idx) =>
        resetKey !== prevProps.resetKeys?.[idx]
      )) {
        this.resetErrorBoundary();
      }
    }

    if (hasError && resetOnPropsChange && prevProps.children !== this.props.children) {
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }

    this.resetTimeoutId = window.setTimeout(() => {
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
      });
    }, 0);
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <div className="flex min-h-svh items-center justify-center bg-background">
          <div className="max-w-md rounded-lg border border-destructive/20 bg-destructive/5 p-6 text-center">
            <div className="mb-4 text-lg font-semibold text-destructive">
              Something went wrong
            </div>
            <div className="mb-4 text-sm text-muted-foreground">
              {isDevelopment() && error ? (
                <details className="mt-4 text-left">
                  <summary className="cursor-pointer font-medium">
                    Error Details (Development)
                  </summary>
                  <pre className="mt-2 overflow-auto text-xs">
                    {error.toString()}
                    {errorInfo?.componentStack}
                  </pre>
                </details>
              ) : (
                'An unexpected error occurred. Please refresh the page or try again later.'
              )}
            </div>
            <button
              onClick={() => window.location.reload()}
              className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return children;
  }
}

/**
 * Hook version of ErrorBoundary for functional components
 */
export function useErrorHandler() {
  return (error: Error, errorInfo?: ErrorInfo) => {
    logger.error('Unhandled error', error, { componentStack: errorInfo?.componentStack });

    // In a real app, report to error monitoring service
    // analytics.track('error', { error, errorInfo });
  };
}
